import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Navigate, Outlet } from 'react-router-dom'

import { Loader } from '@app/components'
import { Switch } from '@app/components/util'

import { useIdleSessionTimeout, useJwtExpirationGuard, useJwtSession, useUserQuery } from '@app/hooks'
import { isJwtExpired } from '@app/hooks/useJwtExpirationGuard'

import { AUTH_PAGE } from '@app/constants/page'
import { AUTH_STATE } from '@app/constants/auth'
import { AUTH_QUERY_KEY } from '@app/constants/queryKeys'

import type { AuthRouteProps, AuthState } from './types'

const { Case } = Switch

export const AuthRoute: React.FC<AuthRouteProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(AUTH_STATE.pending)
  const queryClient = useQueryClient()
  const { session } = useJwtSession()
  const isSessionExpired = !!session && isJwtExpired(session.token)

  const content = children ?? <Outlet />

  useJwtExpirationGuard()
  useIdleSessionTimeout()

  useUserQuery({
    enabled: !!session && !isSessionExpired,
    onSuccess: () => setAuthState(AUTH_STATE.authorized),
    onError: () => setAuthState(AUTH_STATE.notAuthorized)
  })

  useEffect(() => {
    if (session) return

    queryClient.removeQueries({ queryKey: [AUTH_QUERY_KEY.authUser], exact: true })
    setAuthState(AUTH_STATE.notAuthorized)
  }, [queryClient, session])

  return (
    <Switch fallback={ <Loader fullScreen /> }>
      <Case condition={ authState === AUTH_STATE.authorized }>{ content }</Case>

      <Case condition={ authState === AUTH_STATE.notAuthorized }>{ <Navigate to={ AUTH_PAGE.auth } /> }</Case>
    </Switch>
  )
}
